import React from 'react';
import {
  ListChecks,
  Search,
  Landmark,
  ChevronDown,
  FileText,
  CalendarCheck2,
  AlertTriangle,
  Wallet2,
  ArrowRight,
  Banknote,
  CircleDollarSign,
  ImageIcon,
} from 'lucide-react';
import '../../../../../styles/components/auction-payment-detail.scss';
import { apiUrl } from '../../../../../constants/global';

/* ----------------------------------------------------------------------
 * NOTE ON MODELING
 *
 * Each auction can settle to TWO destinations at once, not one:
 *   - `is_client_auction_payment` true  -> the bid amount + item VAT
 *     ("client leg") is owed to the client's own account, while the
 *     service charge + its VAT ("mzadcom leg") is still owed to
 *     Mzadcom. One auction => two legs, two destinations.
 *   - `is_client_auction_payment` false/absent -> everything (bid + VAT
 *     + service charge + VAT) is a single leg owed to Mzadcom.
 *
 * A selection can therefore contribute to BOTH destination blocks in the
 * summary panel at once, even from a single auction. There is still no
 * manual "choose an account" toggle for the bidder — the split is fully
 * determined by the auction's own flag.
 *
 * LEG BALANCE CAVEAT: getWinningAuctions() only returns paid / pending /
 * balance at the whole-auction level, not per leg. Until there's a
 * leg-aware endpoint, remaining balance is split across legs
 * proportionally to each leg's share of the total. This is exact for
 * auctions with nothing paid yet (the common case right after approval)
 * and an approximation once partial payments exist. Swap this for real
 * per-leg figures as soon as the backend exposes them.
 *
 * Auctions with balance_amount <= 0 (nothing left to pay) can't be
 * selected — their checkbox is disabled.
 * -------------------------------------------------------------------- */

// const PAYMENT_HISTORY = [];
// const TIMELINE = [];

const DESTINATION_META = {
  client: { label: 'Client Account', icon: Landmark, tone: 'client' },
  mzadcom: { label: 'Mzadcom Account', icon: Banknote, tone: 'mzadcom' },
};

// Payment Summary's radio-style destination selector uses its own icon set
// to match the approved design (a "$" glyph for Mzadcom there specifically).
const SUMMARY_DEST_ICONS = {
  client: Landmark,
  mzadcom: CircleDollarSign,
};

function money(value) {
  return (Math.round((Number(value) || 0) * 1000) / 1000).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Converts one raw getWinningAuctions() row into the shape the UI renders,
 * including the client/mzadcom leg split described above.
 */
function normalizeAuction(raw) {
  const bidAmount = num(raw.bid_amount);
  const itemVat = num(raw.item_amount_vat);
  const serviceChargeAmount = num(raw.service_charge_amount);
  const serviceChargeVat = num(raw.vat_service_charge);

  const totalAmount =
    num(raw.total_amount) || bidAmount + itemVat + serviceChargeAmount + serviceChargeVat;
  const paid = num(raw.paid_amount);
  const pending = num(raw.pending_approval_amount);
  const balance = num(raw.balance_amount);

  let status = 'pending';
  if (paid > 0 && balance > 0) status = 'partial';
  else if (balance <= 0 && paid > 0) status = 'settled';

  const financeApproval = Number(raw.finance_approved) === 0 && pending > 0;

  const isClientPayment =
    raw.is_client_auction_payment === true || Number(raw.is_client_auction_payment) === 1;

  // Real per-leg figures from the API, when present.
  const hasLegFields =
    raw.client_account_total !== undefined && raw.mzadcom_account_total !== undefined;

  // ---- Build the leg(s) for this auction -----------------------------
  let legs;
  if (isClientPayment) {
    let clientLeg;
    let mzadcomLeg;

    if (hasLegFields) {
      clientLeg = {
        destination: 'client',
        legTotal: num(raw.client_account_total),
        legBalance: num(raw.client_account_balance),
        legPaid: num(raw.client_account_paid),
        legPending: num(raw.client_account_pending),
      };
      mzadcomLeg = {
        destination: 'mzadcom',
        legTotal: num(raw.mzadcom_account_total),
        legBalance: num(raw.mzadcom_account_balance),
        legPaid: num(raw.mzadcom_account_paid),
        legPending: num(raw.mzadcom_account_pending),
      };
    } else {
      // Fallback: proportional approximation (only if API hasn't sent
      // leg-level fields for some reason).
      const clientLegTotal = bidAmount + itemVat;
      const mzadcomLegTotal = serviceChargeAmount + serviceChargeVat;
      const clientShare = totalAmount ? clientLegTotal / totalAmount : 0;
      const mzadcomShare = totalAmount ? mzadcomLegTotal / totalAmount : 0;

      clientLeg = {
        destination: 'client',
        legTotal: clientLegTotal,
        legBalance: Math.max(balance * clientShare, 0),
        legPaid: paid * clientShare,
        legPending: pending * clientShare,
      };
      mzadcomLeg = {
        destination: 'mzadcom',
        legTotal: mzadcomLegTotal,
        legBalance: Math.max(balance * mzadcomShare, 0),
        legPaid: paid * mzadcomShare,
        legPending: pending * mzadcomShare,
      };
    }

    // Drop a leg that has nothing in it (e.g. auction has no service charge)
    legs = [clientLeg, mzadcomLeg].filter((leg) => leg.legTotal > 0);
  } else {
    legs = [
      {
        destination: 'mzadcom',
        legTotal: hasLegFields ? num(raw.mzadcom_account_total) : totalAmount,
        legBalance: hasLegFields ? num(raw.mzadcom_account_balance) : balance,
        legPaid: hasLegFields ? num(raw.mzadcom_account_paid) : paid,
        legPending: hasLegFields ? num(raw.mzadcom_account_pending) : pending,
      },
    ];
  }

  return {
    id: raw.id,
    user_id: raw.user_id,
    title: raw.title,
    groupId: raw.group_id,
    groupName: raw.group_name,
    organizationId: raw.organization_id,
    organizationName: raw.organization_name || raw.client_name,
    iconTone: raw.organization_id % 2 === 0 ? 'teal' : 'olive',
    bidAmount,
    itemVat,
    serviceChargeAmount,
    serviceChargeVat,
    totalAmount,
    paid,
    pending,
    balance,
    approvedDate: raw.approved_date,
    status,
    financeApproval,
    isClientPayment,
    legs,
    selectable: balance > 0,
    raw,
  };
}

class AuctionPaymentDetail extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      search: '',
      sortBy: 'date',
      selectedIds: new Set(),
      expandedIds: new Set(),
      // one amount field per destination, only rendered when that
      // destination is present among the current selection
      amounts: {
        client: { value: '', touched: false },
        mzadcom: { value: '', touched: false },
      },
      // Which destination's amount field is currently shown in the
      // Payment Summary panel. Both destinations still get paid (via the
      // two-step Proceed queue) regardless of which one is selected here —
      // it decides which leg the bidder lands on FIRST in that queue (see
      // handleProceed), not whether a leg gets skipped.
      selectedDestination: null,
    };
  }

  componentDidMount() {
    this.syncAmounts();
  }

  getAuctions = () => {
    const raw = this.props.data || [];
    return raw.map(normalizeAuction);
  };

  getSelectedAuctions = () => {
    const { selectedIds } = this.state;
    return this.getAuctions().filter((a) => selectedIds.has(a.id));
  };

  getVisibleAuctions = () => {
    const { search, sortBy } = this.state;
    let list = this.getAuctions();

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          (a.title || '').toLowerCase().includes(q) ||
          (a.groupName || '').toLowerCase().includes(q) ||
          (a.organizationName || '').toLowerCase().includes(q) ||
          String(a.id).includes(q)
      );
    }

    if (sortBy === 'date') {
      list.sort((a, b) => new Date(a.approvedDate) - new Date(b.approvedDate));
    } else if (sortBy === 'amount') {
      list.sort((a, b) => b.totalAmount - a.totalAmount);
    } else if (sortBy === 'name') {
      list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }

    return list;
  };

  getSummary = () => {
    const selected = this.getSelectedAuctions();

    let totalDue = 0;
    let alreadyPaid = 0;
    let approvalPending = 0;
    const byDestination = {
      client: { count: 0, total: 0 },
      mzadcom: { count: 0, total: 0 },
    };

    selected.forEach((auction) => {
      totalDue += auction.totalAmount;
      alreadyPaid += auction.paid;
      approvalPending += auction.pending;
      // A single auction can contribute to BOTH buckets when it's split
      // into a client leg and a mzadcom leg.
      auction.legs.forEach((leg) => {
        byDestination[leg.destination].count += 1;
        byDestination[leg.destination].total += leg.legBalance;
      });
    });

    const remaining = Math.max(totalDue - alreadyPaid - approvalPending, 0);
    const progressPct = totalDue > 0 ? Math.round((alreadyPaid / totalDue) * 100) : 0;

    return {
      count: selected.length,
      totalDue,
      approvalPending,
      alreadyPaid,
      remaining,
      progressPct,
      byDestination,
    };
  };

  /** Re-seeds any untouched amount field to its destination's full due
   * whenever selection changes, and keeps `selectedDestination` pointed at
   * a destination that's actually present (defaulting to the first one
   * when the previous selection drops out, e.g. client leg deselected). */
  syncAmounts = () => {
    const summary = this.getSummary();
    const present = ['client', 'mzadcom'].filter((d) => summary.byDestination[d].count > 0);

    this.setState((prev) => {
      const amounts = { ...prev.amounts };
      ['client', 'mzadcom'].forEach((dest) => {
        if (!prev.amounts[dest].touched) {
          const due = summary.byDestination[dest].total;
          amounts[dest] = { value: due ? due.toFixed(2) : '', touched: false };
        }
      });

      const selectedDestination = present.includes(prev.selectedDestination)
        ? prev.selectedDestination
        : present[0] || null;

      return { amounts, selectedDestination };
    });
  };

  /** "Paying Bid + VAT" is always accurate for the client leg. For Mzadcom,
   * the wording depends on whether every selected auction routing there is
   * a split leg (is_client_auction_payment = true, so it's just the
   * service charge) or a full, non-split auction amount — a selection can
   * mix both, in which case we fall back to a generic label. */
  getMzadcomLegTitle = () => {
    const mzadcomAuctions = this.getSelectedAuctions().filter((a) =>
      a.legs.some((leg) => leg.destination === 'mzadcom')
    );
    const allSplit = mzadcomAuctions.length > 0 && mzadcomAuctions.every((a) => a.isClientPayment);
    return allSplit ? 'Paying Service Charge + VAT' : 'Paying Full Amount';
  };

  handleToggleSelect = (auction) => {
    if (!auction.selectable) return;

    const selected = this.getSelectedAuctions();

    // Removing is always allowed
    if (this.state.selectedIds.has(auction.id)) {
      this.setState((prev) => {
        const ids = new Set(prev.selectedIds);
        ids.delete(auction.id);
        return { selectedIds: ids };
      }, this.syncAmounts);

      return;
    }

    // Nothing selected yet
    if (selected.length === 0) {
      this.addAuction(auction);
      return;
    }

    const first = selected[0];

    // ------------------------------
    // CLIENT PAYMENT AUCTIONS
    // ------------------------------
    if (auction.isClientPayment) {
      // Cannot mix with normal auctions
      if (!first.isClientPayment) {
        alert('Client payment auctions cannot be mixed with Mzadcom payment auctions.');
        return;
      }

      // Must be same client
      if (auction.organization_id !== first.organization_id) {
        alert('You can only select auctions from the same client.');
        return;
      }

      this.addAuction(auction);
      return;
    }

    // ------------------------------
    // NORMAL AUCTIONS
    // ------------------------------
    if (first.isClientPayment) {
      alert('Cannot mix Mzadcom payment with Client payment auctions.');
      return;
    }

    this.addAuction(auction);
  };

  addAuction = (auction) => {
    this.setState((prev) => {
      const ids = new Set(prev.selectedIds);
      ids.add(auction.id);
      return { selectedIds: ids };
    }, this.syncAmounts);
  };
  isAuctionSelectable = (auction) => {
    const selected = this.getSelectedAuctions();

    if (selected.length === 0) return auction.selectable;

    const first = selected[0];

    if (first.isClientPayment) {
      return (
        auction.isClientPayment &&
        auction.organizationId === first.organizationId &&
        auction.selectable
      );
    }

    return !auction.isClientPayment && auction.selectable;
  };

  handleSelectAll = (e) => {
    const checked = e.target.checked;

    if (!checked) {
      this.setState({ selectedIds: new Set() }, this.syncAmounts);
      return;
    }

    const visible = this.getVisibleAuctions().filter((a) => a.selectable);

    if (!visible.length) return;

    const selected = this.getSelectedAuctions();

    const first = selected.length ? selected[0] : visible[0];

    let allowed;

    if (first.isClientPayment) {
      allowed = visible.filter(
        (a) => a.isClientPayment && a.organizationId === first.organizationId
      );
    } else {
      allowed = visible.filter((a) => !a.isClientPayment);
    }

    this.setState(
      {
        selectedIds: new Set(allowed.map((a) => a.id)),
      },
      this.syncAmounts
    );
  };

  handleToggleExpand = (id) => {
    this.setState((prev) => {
      const next = new Set(prev.expandedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { expandedIds: next };
    });
  };

  handleAmountChange = (destination, value) => {
    this.setState((prev) => ({
      amounts: { ...prev.amounts, [destination]: { value, touched: true } },
    }));
  };

  handleQuickFill = (destination, mode) => {
    const summary = this.getSummary();
    const due = summary.byDestination[destination].total;
    const value = mode === 'half' ? due / 2 : due;

    this.setState((prev) => ({
      amounts: { ...prev.amounts, [destination]: { value: value.toFixed(2), touched: true } },
    }));
  };

  handleProceed = () => {
    const selected = this.getSelectedAuctions();
    const { amounts, selectedDestination } = this.state;
    const summary = this.getSummary();

    // Build one "payable row" per leg, not per auction — a split auction
    // shows up once in clientLegRows and once in mzadcomLegRows, each
    // carrying only that leg's own amount so downstream (PaymentDistribution)
    // never double-counts or leaks the other leg's balance.
    const clientLegRows = [];
    const mzadcomLegRows = [];

    selected.forEach((auction) => {
      auction.legs.forEach((leg) => {
        const row = {
          ...auction.raw,
          // Carry the RESOLVED organization name through explicitly —
          // relying on `...auction.raw` alone loses the
          // organization_name/client_name fallback that normalizeAuction
          // already worked out, so PaymentDistribution could end up with
          // no name at all if the raw row only had one of the two fields.
          organization_name: auction.organizationName,
          // Override so any code downstream reading these standard field
          // names automatically gets the LEG amount, not the whole-auction
          // amount.
          balance_amount: leg.legBalance,
          total_amount: leg.legTotal,
          total_paid: leg.legPaid,
          pending_approval_amount: leg.legPending,
          _leg_destination: leg.destination,
        };
        if (leg.destination === 'client') clientLegRows.push(row);
        else mzadcomLegRows.push(row);
      });
    });

    const clientStep =
      clientLegRows.length > 0
        ? {
            destination: 'client',
            auctions: clientLegRows,
            amount: Number(amounts.client.value) || 0,
            due: summary.byDestination.client.total,
          }
        : null;

    const mzadcomStep =
      mzadcomLegRows.length > 0
        ? {
            destination: 'mzadcom',
            auctions: mzadcomLegRows,
            amount: Number(amounts.mzadcom.value) || 0,
            due: summary.byDestination.mzadcom.total,
          }
        : null;

    // Both legs still get paid regardless of which radio is selected in
    // the summary — nothing here skips a leg. What this DOES control is
    // ORDER: whichever destination the bidder currently has selected goes
    // first in the queue, so picking "Mzadcom Account" and proceeding
    // takes them straight to the Mzadcom (service charge + VAT) screen
    // instead of always defaulting to the client leg first.
    const orderedSteps =
      selectedDestination === 'mzadcom' ? [mzadcomStep, clientStep] : [clientStep, mzadcomStep];
    const steps = orderedSteps.filter(Boolean);

    if (this.props.onProceed) {
      this.props.onProceed(steps);
    } else {
      // eslint-disable-next-line no-console
      console.log('Proceeding to pay', steps);
    }
  };

  renderStatusPill(status) {
    if (status === 'partial') {
      return <span className="ap-pill ap-pill--partial">PARTIAL</span>;
    }
    if (status === 'settled') {
      return <span className="ap-pill ap-pill--partial">SETTLED</span>;
    }
    return <span className="ap-pill ap-pill--pending">PENDING</span>;
  }

  renderApprovedPill(auction) {
    if (!auction.approvedDate) return null;
    return (
      <span className="ap-pill ap-pill--due">
        <CalendarCheck2 size={12} /> Approved {auction.approvedDate}
      </span>
    );
  }

  renderAuctionCard = (auction) => {
    const selectable = this.isAuctionSelectable(auction);

    const { selectedIds, expandedIds } = this.state;
    const isSelected = selectedIds.has(auction.id);
    const isExpanded = expandedIds.has(auction.id);

    const paidPct = auction.totalAmount ? (auction.paid / auction.totalAmount) * 100 : 0;
    const pendingPct = auction.totalAmount ? (auction.pending / auction.totalAmount) * 100 : 0;

    return (
      <div
        key={auction.id}
        className={`ap-auction-card${isSelected ? ' ap-auction-card--selected' : ''}${
          !selectable ? ' ap-auction-card--disabled' : ''
        }`}
      >
        <div className="ap-auction-card__top">
          <label className={`ap-checkbox${!selectable ? ' ap-checkbox--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!selectable}
              onChange={() => this.handleToggleSelect(auction)}
            />
            <span className="ap-checkbox__box" />
          </label>

          <span className={`ap-auction-card__icon ap-auction-card__icon--${auction.iconTone}`}>
            <Landmark size={20} />
          </span>

          <div className="ap-auction-card__identity">
            <p className="ap-auction-card__title" title={auction.title}>
              {auction.title}
            </p>
            <p className="ap-auction-card__sub">
              #{auction.id} &middot; {auction.organizationName}
            </p>
          </div>

          <div className="ap-auction-card__amount">
            <span className="ap-auction-card__amount-value">{money(auction.totalAmount)}</span>
            <span className="ap-auction-card__amount-label">TOTAL DUE (OMR)</span>
          </div>
        </div>

        <div className="ap-progress">
          <i className="ap-progress__paid" style={{ width: `${paidPct}%` }} />
          <i
            className="ap-progress__pending"
            style={{ width: `${pendingPct}%`, insetInlineStart: `${paidPct}%` }}
          />
        </div>

        <div className="ap-auction-card__stats">
          <div className="ap-stat">
            <span className="ap-stat__label">PAID</span>
            <span className="ap-stat__value ap-stat__value--paid">{money(auction.paid)}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat__label">PENDING APPROVAL</span>
            <span className="ap-stat__value ap-stat__value--pending">{money(auction.pending)}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat__label">REMAINING</span>
            <span className="ap-stat__value">{money(auction.balance)}</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat__label">TOTAL</span>
            <span className="ap-stat__value">{money(auction.totalAmount)}</span>
          </div>
        </div>

        <div className="ap-auction-card__footer">
          <div className="ap-auction-card__badges">
            {this.renderStatusPill(auction.status)}
            {auction.financeApproval && (
              <span className="ap-pill ap-pill--finance">
                <AlertTriangle size={12} /> AWAITING FINANCE APPROVAL
              </span>
            )}
            {/* One badge per leg — a split auction shows both destinations
                at once, each with its own portion of the balance. */}
            {auction.legs.map((leg) => {
              const meta = DESTINATION_META[leg.destination];
              return (
                <span
                  key={leg.destination}
                  className={`ap-pill ap-pill--dest ap-pill--dest-${meta.tone}`}
                >
                  <meta.icon size={12} /> {meta.label}: {money(leg.legBalance)}
                </span>
              );
            })}
            {this.renderApprovedPill(auction)}
          </div>

          <button
            type="button"
            className={`ap-expand-btn${isExpanded ? ' ap-expand-btn--open' : ''}`}
            onClick={() => this.handleToggleExpand(auction.id)}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <ChevronDown size={16} />
          </button>
        </div>

        {isExpanded && (
          <div className="ap-auction-card__details">
            <div className="ap-auction-card__details-col">
              <div className="ap-detail-row">
                <span>Final Bid Amount</span>
                <strong>{money(auction.bidAmount)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>VAT</span>
                <strong>{money(auction.itemVat)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>Service Charge</span>
                <strong>{money(auction.serviceChargeAmount)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>Service Charge VAT</span>
                <strong>{money(auction.serviceChargeVat)}</strong>
              </div>
            </div>
            <div className="ap-auction-card__details-col">
              <div className="ap-detail-row">
                <span>Paid (Approved)</span>
                <strong>{money(auction.paid)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>Pending Approval</span>
                <strong>{money(auction.pending)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>Remaining</span>
                <strong>{money(auction.balance)}</strong>
              </div>
              <div className="ap-detail-row">
                <span>Group</span>
                <strong>{auction.groupName}</strong>
              </div>
            </div>

            <div className="ap-auction-card__details-actions">
              <button
                type="button"
                className="ap-doc-btn"
                onClick={() =>
                  window.open(
                    apiUrl +
                      'my_winnings/pdf?' +
                      new URLSearchParams({
                        id: auction.user_id,
                        auction: auction.id,
                      }).toString(),
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                <FileText size={14} />
                <span>Mzadcom Invoice</span>
              </button>
              {/* <button type="button" className="ap-doc-btn">
                <Receipt size={14} /> Receipt
              </button> */}
            </div>
          </div>
        )}
      </div>
    );
  };

  /** One radio-style row per destination actually present in the current
   * selection. Selecting a row only changes which one's amount field is
   * shown below — it doesn't opt that destination out of payment, both
   * still flow through the Proceed queue. When only Mzadcom is present
   * (the non-split, "everything goes to Mzadcom" case) this renders a
   * single row with nothing to toggle. */
  renderDestinationOption = (destination) => {
    const summary = this.getSummary();
    const info = summary.byDestination[destination];
    if (info.count === 0) return null;

    const meta = DESTINATION_META[destination];
    const Icon = SUMMARY_DEST_ICONS[destination];
    const isSelected = this.state.selectedDestination === destination;
    const title = destination === 'client' ? 'Paying Bid + VAT' : this.getMzadcomLegTitle();

    return (
      <button
        type="button"
        key={destination}
        className={`ap-dest-option ap-dest-option--${meta.tone}${
          isSelected ? ' ap-dest-option--selected' : ''
        }`}
        onClick={() => this.setState({ selectedDestination: destination })}
      >
        <span className="ap-dest-option__radio" aria-hidden="true" />
        <span className={`ap-dest-option__icon ap-dest-option__icon--${meta.tone}`}>
          <Icon size={16} />
        </span>
        <span className="ap-dest-option__text">
          <strong>{title}</strong>
          <span>To {meta.label}</span>
        </span>
        <span className="ap-dest-option__amount">{money(info.total)}</span>
      </button>
    );
  };

  /** The editable amount card for whichever destination is currently
   * selected in the radio list above. */
  renderAmountField = (destination) => {
    const summary = this.getSummary();
    const info = summary.byDestination[destination];
    if (info.count === 0) return null;

    const meta = DESTINATION_META[destination];
    const amountState = this.state.amounts[destination];
    const afterThisPayment = Math.max(info.total - (Number(amountState.value) || 0), 0);

    return (
      <div className="ap-amount-card ap-amount-field">
        <label>PAYMENT AMOUNT FOR {meta.label.toUpperCase()} (OMR)</label>
        <div className="ap-amount-input">
          <span>OMR</span>
          <input
            type="number"
            value={amountState.value}
            onChange={(e) => this.handleAmountChange(destination, e.target.value)}
            min="0"
            step="0.01"
          />
        </div>

        <div className="ap-quickfill">
          <button type="button" onClick={() => this.handleQuickFill(destination, 'half')}>
            50%
          </button>
          <button type="button" onClick={() => this.handleQuickFill(destination, 'full')}>
            Full Amount
          </button>
        </div>

        <div className="ap-after-payment">
          <span>After This Payment</span>
          <strong>{money(afterThisPayment)}</strong>
        </div>
      </div>
    );
  };

  render() {
    const { search, sortBy } = this.state;
    const visibleAuctions = this.getVisibleAuctions();
    const selectableVisible = visibleAuctions.filter((a) => a.selectable);
    const allVisibleSelected =
      selectableVisible.length > 0 &&
      selectableVisible.every((a) => this.state.selectedIds.has(a.id));
    const summary = this.getSummary();

    const hasClientAmount =
      summary.byDestination.client.count === 0 || Number(this.state.amounts.client.value) > 0;
    const hasMzadcomAmount =
      summary.byDestination.mzadcom.count === 0 || Number(this.state.amounts.mzadcom.value) > 0;
    const canProceed = summary.count > 0 && hasClientAmount && hasMzadcomAmount;

    return (
      <div className="ap-page">
        <div className="ap-shell">
          <div className="ap-columns">
            {/* ---------------- Left: Auction Selection ---------------- */}
            <section className="ap-panel ap-panel--selection">
              <div className="ap-panel__head">
                <h2>
                  <ListChecks size={18} /> Auction Selection
                </h2>
                <span className="ap-panel__count">{summary.count} selected</span>
              </div>

              <div className="ap-toolbar">
                <div className="ap-search">
                  <Search size={16} />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => this.setState({ search: e.target.value })}
                    placeholder="Search by auction name or number..."
                  />
                </div>

                <select
                  className="ap-sort"
                  value={sortBy}
                  onChange={(e) => this.setState({ sortBy: e.target.value })}
                >
                  <option value="date">Sort: Date</option>
                  <option value="amount">Sort: Amount</option>
                  <option value="name">Sort: Name</option>
                </select>

                <label className="ap-select-all">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={this.handleSelectAll}
                    disabled={
                      this.getVisibleAuctions().filter((a) => this.isAuctionSelectable(a))
                        .length === 0
                    }
                  />
                  <span>Select all</span>
                </label>
              </div>

              <div className="ap-auction-list">
                {visibleAuctions.map((a) => this.renderAuctionCard(a))}

                {visibleAuctions.length === 0 && (
                  <div className="ap-empty">
                    <ImageIcon size={30} />
                    <p>No auctions match your search.</p>
                  </div>
                )}
              </div>
            </section>

            {/* ---------------- Right: Payment Summary ---------------- */}
            <aside className="ap-panel ap-panel--summary">
              <div className="ap-panel__head">
                <h2>
                  <Wallet2 size={18} /> Payment Summary
                </h2>
              </div>

              <div className="ap-progress-card">
                <div className="ap-ring" style={{ '--pct': summary.progressPct }}>
                  <span>{summary.progressPct}%</span>
                </div>
                <div className="ap-progress-card__body">
                  <span className="ap-progress-card__label">PAYMENT PROGRESS</span>
                  <span className="ap-progress-card__value">
                    {money(summary.alreadyPaid)} / {money(summary.totalDue)}
                  </span>
                </div>
              </div>

              <div className="ap-summary-rows">
                <div className="ap-summary-row">
                  <span>Selected Auctions</span>
                  <strong>{summary.count}</strong>
                </div>
                <div className="ap-summary-row">
                  <span>Total Due</span>
                  <strong>{money(summary.totalDue)}</strong>
                </div>
                <div className="ap-summary-row">
                  <span>Already Paid</span>
                  <strong>{money(summary.alreadyPaid)}</strong>
                </div>
                <div className="ap-summary-row">
                  <span>Pending Approval</span>
                  <strong>{money(summary.approvalPending)}</strong>
                </div>
                <div className="ap-summary-row">
                  <span>Remaining</span>
                  <strong className="ap-summary-row__accent">{money(summary.remaining)}</strong>
                </div>
              </div>

              {summary.count === 0 ? (
                <div className="ap-empty">
                  <ListChecks size={26} />
                  <p>Select one or more auctions to see payment details.</p>
                </div>
              ) : (
                <div className="ap-destination-blocks">
                  <div className="ap-dest-options">
                    {['client', 'mzadcom'].map((d) => this.renderDestinationOption(d))}
                  </div>
                  {/* {this.state.selectedDestination &&
                    this.renderAmountField(this.state.selectedDestination)} */}
                </div>
              )}

              <button
                type="button"
                className="ap-proceed-btn"
                onClick={this.handleProceed}
                disabled={!canProceed}
              >
                <ArrowRight size={16} /> Proceed to Pay
              </button>
            </aside>
          </div>

          {/* ---------------- Activity & History ---------------- */}
          {/* NOTE: getWinningAuctions() has no timeline/history data — these
              sections stay empty-state until a real endpoint exists. */}
          {/* <div className="ap-activity-head">
            <History size={18} /> Activity &amp; History
          </div>

          <div className="ap-activity-columns">
            <section className="ap-panel">
              <div className="ap-panel__head ap-panel__head--sub">
                <h3>
                  <Clock3 size={16} /> Payment Timeline
                </h3>
              </div>

              {TIMELINE.length === 0 ? (
                <div className="ap-empty">
                  <Clock3 size={28} />
                  <p>No timeline events yet.</p>
                </div>
              ) : (
                <ul className="ap-timeline">
                  {TIMELINE.map((step) => {
                    const Icon = step.icon;
                    return (
                      <li
                        key={step.title}
                        className={`ap-timeline__item${step.done ? '' : ' ap-timeline__item--pending'}`}
                      >
                        <span className="ap-timeline__dot">
                          <Icon size={15} />
                        </span>
                        <div className="ap-timeline__body">
                          <p className="ap-timeline__title">{step.title}</p>
                          <p className="ap-timeline__meta">
                            {step.actor} &middot; {step.when}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="ap-panel">
              <div className="ap-panel__head ap-panel__head--sub">
                <h3>
                  <History size={16} /> Payment History
                </h3>
              </div>

              {PAYMENT_HISTORY.length === 0 ? (
                <div className="ap-empty">
                  <History size={28} />
                  <p>No payment history yet.</p>
                </div>
              ) : (
                <ul className="ap-history">
                  {PAYMENT_HISTORY.map((entry) => (
                    <li key={entry.title + entry.date} className="ap-history__item">
                      <div>
                        <p className="ap-history__title">{entry.title}</p>
                        <p className="ap-history__meta">
                          {entry.date} &middot; {entry.method}
                        </p>
                      </div>
                      <span className="ap-history__amount">{money(entry.amount)} OMR</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div> */}
        </div>
      </div>
    );
  }
}

export default AuctionPaymentDetail;
